/**
 * Module for handling the detail view of a trip and its segments
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2020
 */

import { length } from '@turf/length';
import { along } from '@turf/along';
import { isEqual } from 'lodash';
import { TripsApi } from '@/services/api-trips.js';
import { SegmentsApi } from '@/services/api-segments.js';
import { useSourceClassification } from '@/composables/useSourceClassification';

/**
 * Local state data
 * -----------------------------------------------------
 */

function defaultStateFactory() {
  return {
    current: {
      trip: {
        segments: [],
      },
      owner: {},
    },
  };
}

export const state = defaultStateFactory();

/**
 * Computed properties of the local state
 * -----------------------------------------------------
 */
export const getters = {
  /**
   * Pull out only the trip itself
   * @param {Object} state
   * @returns {Object}
   */
  trip: (state) => state.current.trip,

  /**
   * Grab the owner of the trip
   * @param {Object} state
   * @param {Object} getters
   * @returns {Object}
   */
  owner: (state, getters) => getters.trip.owner,

  /**
   * Pull out the grouped segments from the trip data
   * @param {Object} state
   * @param {Object} getters
   * @returns {Array}
   */
  segments: (state, getters) => getters.trip.segments,

  /**
   * Flatten all the segments into a single array and remove any Psuedo segments
   * @param {Object} state
   * @param {Object} getters
   * @returns {Array}
   */
  flatSegments: (state, getters) => {
    let flattened = [];
    getters.segments.forEach((DateGroup) => {
      // we don't want psuedo-segments
      let filtered = DateGroup.segments.filter(
        (segment) => segment.subType !== 'CheckOut' && segment.subType !== 'Overnight'
      );
      flattened = flattened.concat(filtered);
    });
    return flattened;
  },

  /**
   * Format the flattened segments into a GeoJSON collection for map use
   * @param {Object} state
   * @param {Object} getters
   * @returns {Object}
   */
  geoDataSegments: (state, getters) => {
    let geoJSON = {
      type: 'FeatureCollection',
      features: [],
    };

    getters.flatSegments.forEach((segment) => {
      if (segment.startDateTime && segment.startLongitude && segment.startLatitude) {
        geoJSON.features.push(_getGeoSegmentFeature(segment, 'start'));
      }

      if (segment.endDateTime && segment.endLongitude && segment.endLatitude) {
        geoJSON.features.push(_getGeoSegmentFeature(segment, 'end'));
      }
    });

    return geoJSON;
  },

  geoDataRoutes: (state, getters) => {
    let geoJSON = {
      type: 'FeatureCollection',
      features: [],
    };

    getters.flatSegments.forEach((segment) => {
      if (_hasValidStartEnd(segment)) {
        geoJSON.features.push(_getGeoRouteFeature(segment));
      }
    });

    return geoJSON;
  },
};

/**
 * Synchronous changes to the local state
 * -----------------------------------------------------
 */
export const mutations = {
  updateCurrent(state, trip) {
    state.current = trip;
  },

  resetState(state, defaultState) {
    state = defaultState;
  },
};

/**
 * Async CRUD actions against the api
 * -----------------------------------------------------
 */
export const actions = {
  show({ getters, commit }, id) {
    return TripsApi.show(id).then((response) => {
      commit('updateCurrent', response);
      return getters.trip;
    });
  },

  destroy({ state, dispatch }, id) {
    return TripsApi.destroy(id).then(() => {
      dispatch('reset');
      return state.current;
    });
  },

  destroySegment({ dispatch, getters }, { id, type }) {
    return SegmentsApi.destroy(id, type).then(() => {
      return dispatch.show(getters.trip.id);
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('resetState', defaultStateFactory());
  },
};

// ////////////////// //

/**
 * Build up an individual GeoJSON feature per segment
 * @param {Object} segment - segment data
 * @param {('start'|'end')} leg - which leg of the journey should I get the data from
 * @returns {Object} GeoJSON formatted feature for an individual segment point
 */
function _getGeoSegmentFeature(segment, leg) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [Number(segment[`${leg}Longitude`]), Number(segment[`${leg}Latitude`])],
    },
    properties: {
      // pull out properties to use in popups
      title: segment.provider,
      description: _formatAddress(segment[`${leg}City`], segment[`${leg}StateProvence`]),
      // include all the rest of the segment data
      ...segment,
    },
  };
}

/**
 * @name formatAddress
 * @desc Properly formats address
 * @param {string} city
 * @param {string|number=0} stateProvince - either the location or a '0' to inidicate it not being set
 */
function _formatAddress(city, stateProvince) {
  let address = city;
  if (stateProvince?.length && stateProvince !== '0') {
    address += `, ${stateProvince}`;
  }

  return address;
}

/**
 * Check for both start and end lat/long and makes sure they are different
 * @param {Object} segment - Segment data
 * @returns {boolean} Does the segment have a valid start and end location
 */
function _hasValidStartEnd(segment) {
  let isValid = false;
  let missingDataCount = 0;

  ['startLongitude', 'startLatitude', 'endLongitude', 'endLatitude'].forEach((key) => {
    const data = segment[key];
    if (key in segment === false || typeof data === 'undefined' || data === null) {
      missingDataCount += 1;
    }
  });

  // make sure that the start and end points are different
  const start = [segment.startLongitude, segment.startLatitude];
  const end = [segment.endLongitude, segment.endLatitude];
  if (!missingDataCount && !isEqual(start, end)) {
    isValid = true;
  }

  return isValid;
}

/**
 * Check for both start and end lat/long and makes sure they are different
 * @param {Object} segment - Segment data
 * @returns {Object} Does the segment have a valid start and end location
 */
function _getGeoRouteFeature(segment) {
  let start = [Number(segment.startLongitude), Number(segment.startLatitude)];
  let end = [Number(segment.endLongitude), Number(segment.endLatitude)];
  let { styles } = useSourceClassification(segment);

  let route = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [start, end],
    },
    properties: {
      color: styles.value.color,
    },
  };

  // Make an arc line out of air travel
  if (segment.type === 'Air') {
    const LINE_DISTANCE = length(route, { units: 'kilometers' });
    const STEPS = 500;
    let arc = [];

    for (let i = 0; i < LINE_DISTANCE; i += LINE_DISTANCE / STEPS) {
      let linePoint = along(route, i, 'kilometers');
      arc.push(linePoint.geometry.coordinates);
    }

    route.geometry.coordinates = arc;
  }

  return route;
}

// /**
//  * Find the hex color code for a specific type of segment
//  * @param {Object} segment - Segment data
//  * @returns {string} Color hex code
//  */
// function _getSegmentColor(segment) {
//   let { color } = useSourceClassification(segment);
//   return color;
// }
